<template>
  <div>
    <div v-if="combineInitCode === 1">
      <v-layout row wrap v-if="hasPlans" class="planBar">
        <v-btn round color="primary" @click.prevent="doBack">
          <v-icon left dark>mdi-chevron-left</v-icon>
          Back
        </v-btn>
        <v-spacer></v-spacer>
        <v-subheader>Show:</v-subheader>
        <v-btn flat :color="showActive === 1 ? 'primary' : 'secondary'" @click.prevent="showActive = 1">
          Plans
          <v-icon right>mdi-view-stream</v-icon>
        </v-btn>
        <v-btn flat :color="showActive === 2 ? 'primary' : 'secondary'" @click.prevent="showActive = 2">
          Manual
          <v-icon right>mdi-pencil</v-icon>
        </v-btn>
      </v-layout>
      <v-layout row wrap class="mb-4" v-else>
        <v-btn round color="primary" @click.prevent="doBack">
          <v-icon left dark>mdi-chevron-left</v-icon>
          Back
        </v-btn>
      </v-layout>
      <transition name="slide-x-transition" mode="out-in">
        <v-container key="plans" fluid grid-list-md v-if="showActive === 1">
          <v-layout row wrap>
            <v-flex xs12>
              <common-plans :plans="formatPlans" :limitsText="formatLimitsText" :selected="planCode" :currPrefix="currPrefix" iconType="MOTOR" @selectPlan="doSelectPlan"></common-plans>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-btn round color="primary" @click.prevent="doBack">
              <v-icon left dark>mdi-chevron-left</v-icon>
              Back
            </v-btn>
          </v-layout>
        </v-container>
        <v-container key="manual" fluid grid-list-md v-else-if="showActive === 2">
          <v-layout row wrap>
            <v-flex xs12>
              <stats v-model="motorForm.statistics" ref="stats"></stats>
            </v-flex>
            <v-flex xs12>
              <excess v-model="motorForm.excess" ref="excess"></excess>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-btn round color="primary" @click.prevent="doBack">
              <v-icon left dark>mdi-chevron-left</v-icon>
              Back
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn round color="primary" type="submit" @click.prevent="doProceed" :disabled="proceedDisabled">
              Get Premium
              <v-icon right dark>mdi-chevron-right</v-icon>
            </v-btn>
          </v-layout>
        </v-container>
      </transition>
    </div>
    <loader v-else :height="425" :init="doInit" loadText="Loading Statistics and Excess" errorText="Failed to load Statistics / Excess" :loading="combineInitCode === 0"></loader>
  </div>
</template>

<script>
import clone from 'clone'
import { AGENCY_QUOTATION_LIFECYCLE, QUOTE_MOTOR_STATISTICS, QUOTE_MOTOR_EXCESS, QUOTE_MOTOR_PLAN } from '@/store/types'
import MessageMixin from '@/mixins/message'
import { SaveQuoteMixin } from '../../shared/saveQuoteMixin'
import Stats from './sub/Stats'
import Excess from './sub/Excess'
import CommonPlans from '../../shared/plans/CommonPlans'
import { extractRecord } from '@/utils/extract'

export default {
  data() {
    return {
      labels: {
        excess: 'Excess'
      },
      motorForm: {
        statistics: [],
        excess: []
      },
      showActive: 2,
      planCode: undefined
    }
  },
  computed: {
    quoteParam() {
      return this.$store.state.agency.quotation.motorParam
    },
    globalForm() {
      return this.$store.state.agency.quotation.motorForm
    },
    currPrefix() {
      return this.$store.state.agency.quotation.commonInfo.currPrefix
    },
    proceedDisabled() {
      // If stats / excess are not loaded yet or has error, do not proceed
      if (this.quoteParam.statisticsInit !== 1 || this.quoteParam.excessInit !== 1) {
        return true
      } else {
        return false
      }
    },
    combineInitCode() {
      if (this.quoteParam.statisticsInit === 0 || this.quoteParam.excessInit === 0 || this.quoteParam.plans.init === 0) {
        return 0
      } else if (this.quoteParam.statisticsInit === 2 || this.quoteParam.excessInit === 2) {
        return 2
      } else {
        return 1
      }
    },
    hasPlans() {
      if (this.quoteParam.plans.init === 1) {
        if (this.quoteParam.plans.plans.length > 0) {
          return true
        }
      }
      return false
    },
    formatPlans() {
      if (this.hasPlans) {
        let result = []
        const { plans } = this.quoteParam.plans
        for (const plan of plans) {
          // Formatting excess values
          const excess = clone(plan.excess)
          if (excess) {
            Object.keys(excess).forEach((key) => {
              if (excess[key]) {
                excess[key] = {
                  uomAmt: excess[key]
                }
              }
            })
          }
          // Formatting statistic values
          const statistics = clone(plan.statistics)
          if (statistics) {
            Object.keys(statistics).forEach((key) => {
              const statRecord = extractRecord(this.quoteParam.statistics, key)
              if (statRecord) {
                const result = extractRecord(statRecord.statsVal, statistics[key])
                if (result !== null) {
                  statistics[key] = {
                    strValue: result.label
                  }
                } else {
                  statistics[key] = {
                    strValue: 'NA'
                  }
                }
              } else {
                statistics[key] = {
                  strValue: 'NA'
                }
              }
            })
          }
          const limits = Object.assign({}, excess, statistics)
          result.push({
            planNo: plan.planCode,
            planDesc: plan.planDesc,
            limits,
            premium: plan.premium
          })
        }
        return result
      } else {
        return []
      }
    },
    formatLimitsText() {
      if (this.hasPlans) {
        let result = []
        // Format Statistics
        const { statistics } = this.quoteParam.plans
        if (statistics && statistics.length > 0) {
          for (const statistic of statistics) {
            const statCode = extractRecord(this.quoteParam.statistics, statistic)
            if (statCode) {
              result.push({
                value: statistic,
                label: statCode.label
              })
            } else {
              result.push({
                value: statistic,
                label: statistic
              })
            }
          }
        }
        // Format Excess
        const { excess } = this.quoteParam.plans
        if (excess && excess.length > 0) {
          for (const ex of excess) {
            const exCode = extractRecord(this.quoteParam.excess, ex)
            if (exCode) {
              result.push({
                value: ex,
                label: exCode.label
              })
            } else {
              result.push({
                value: ex,
                label: ex
              })
            }
          }
        }
        return result
      } else {
        return []
      }
    }
  },
  watch: {
    'hasPlans': function (value) {
      if (value === true) {
        this.showActive = 1
        this.planCode = this.$store.state.agency.quotation.motorForm.mtrPlan
      }
    }
  },
  methods: {
    doInit() {
      // There are inits inside stats and excess, but we will do the combinations of the init here
      // This is to cater for motor plans (the plans needs both to be inited)
      if (this.quoteParam.statisticsInit !== 1) {
        this.$store.dispatch('motorQuoteStatistics', this.globalForm.busCls)
      }
      if (this.quoteParam.excessInit !== 1) {
        this.$store.dispatch('motorQuoteExcess', { busCls: this.globalForm.busCls, cover: this.globalForm.cover })
      }
      if (this.hasPlans) {
        this.showActive = 1
        this.planCode = this.$store.state.agency.quotation.motorForm.mtrPlan
      }
    },
    doBack() {
      this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 3)
      this.$router.push({ name: 'motorQuoteVehicle' })
    },
    async doProceed() {
      const statsValidation = await this.$refs.stats.doValidate()
      const excessValidation = await this.$refs.excess.doValidate()
      if (statsValidation && excessValidation) {
        this.$store.commit(QUOTE_MOTOR_PLAN, null)
        this.$store.commit(QUOTE_MOTOR_STATISTICS, this.motorForm.statistics)
        this.$store.commit(QUOTE_MOTOR_EXCESS, this.motorForm.excess)
        this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 5)
        this.$router.push({ name: 'motorQuotePremium' })
      } else {
        this.promptFieldCheck()
      }
    },
    async doPartialSave() {
      // No mandatory validation here. Commit whatever thats on screen and do neccesary actions before saving
      // Must return true/false
      try {
        this.$store.commit(QUOTE_MOTOR_STATISTICS, this.motorForm.statistics)
        this.$store.commit(QUOTE_MOTOR_EXCESS, this.motorForm.excess)
        await this.$store.dispatch('saveQuote')
        return true
      } catch (err) {
        return false
      }
    },
    doSelectPlan(obj) {
      if (obj) {
        this.planCode = obj.value
        if (obj.proceed) {
          this.doProceedPlan()
        }
      }
    },
    async doProceedPlan() {
      if (this.planCode) {
        const plan = this.quoteParam.plans.plans.find(x => x.planCode === this.planCode)
        if (plan) {
          // Statistics
          this.motorForm.statistics = []
          if (plan.statistics) {
            Object.keys(plan.statistics).forEach((key) => {
              const value = plan.statistics[key]
              this.motorForm.statistics.push({
                key,
                value
              })
            })
          }
          // Excess
          this.motorForm.excess = []
          if (plan.excess) {
            Object.keys(plan.excess).forEach((key) => {
              const value = plan.excess[key]
              this.motorForm.excess.push({
                key,
                value
              })
            })
          }
          // For plan selection, we have to clear off additional bens (so that premium is consistent with the final screen)
          this.$store.commit(QUOTE_MOTOR_PLAN, plan.planCode)
          this.$store.commit(QUOTE_MOTOR_STATISTICS, this.motorForm.statistics)
          this.$store.commit(QUOTE_MOTOR_EXCESS, this.motorForm.excess)
          this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 5)
          this.$router.push({ name: 'motorQuotePremium' })
        } else {
          this.$snotify.error('Error in Plan Selection')
        }
      } else {
        this.$snotify.error('Please select a Plan')
      }
    }
  },
  beforeMount() {
    // Check quote lifecycle (might be using back link or url)
    if (this.$store.state.agency.quotation.quoteLifeCycle !== 4) {
      this.$store.commit(AGENCY_QUOTATION_LIFECYCLE, 4)
    }
    this.doInit()
  },
  mixins: [
    MessageMixin,
    SaveQuoteMixin
  ],
  components: {
    Stats,
    Excess,
    CommonPlans
  }
}
</script>

<style lang="scss">
.excessDiplay {
  padding-top: 18px;
}
.planBar {
  padding: 12px;
  margin-bottom: 50px !important;
}
</style>
